import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { dispatchFetch, T, F, bd, fmtTime } from '@/lib/dispatchApi';
import { localISO } from '@/lib/localDate';
import DispatchButton from '@/components/dispatch/DispatchButton';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function getMondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}

function toISO(d) { return localISO(d); }

// Routine statuses are plain text; nothing here needs action, so no colour.
function pillForStatus(status) {
  if (status === 'completed') return { label: 'Delivered', fg: T.moonMute, dot: T.moonFaint };
  if (status === 'in_progress') return { label: 'In progress', fg: T.moonSoft, dot: T.moon };
  return { label: 'Scheduled', fg: T.moonMute, dot: T.nightHairS };
}

export default function DispatchSchedulePage() {
  const navigate = useNavigate();
  const today = new Date();
  const [weekMonday, setWeekMonday] = useState(getMondayOf(today));
  const [selected, setSelected] = useState(toISO(today));
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekMonday);
    d.setDate(weekMonday.getDate() + i);
    return d;
  });

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatchFetch('/api/jobs');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setAllJobs(await res.json());
    } catch (e) {
      console.error('Schedule fetch', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const jobsForDate = (iso) => allJobs.filter(j => j.scheduled_date === iso);
  const selectedJobs = jobsForDate(selected).sort((a, b) => (a.time_window_start || '').localeCompare(b.time_window_start || ''));
  const selectedLabel = new Date(selected + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const weekNum = getWeekNumber(weekMonday);
  const todayISO = toISO(today);

  const px = 24;
  const goWeek = (delta) => {
    const m = new Date(weekMonday); m.setDate(m.getDate() + delta * 7); setWeekMonday(m);
    const thisWeek = toISO(getMondayOf(today)) === toISO(m);
    setSelected(thisWeek ? todayISO : toISO(m));
  };
  const goToday = () => { setWeekMonday(getMondayOf(today)); setSelected(todayISO); };
  const nextShoot = selectedJobs.find(j => j.status !== 'completed');
  const mainAction = selected !== todayISO
    ? <DispatchButton variant="main" data-testid="schedule-today" onClick={goToday}>Back to today</DispatchButton>
    : nextShoot
      ? <DispatchButton variant="main" data-testid="schedule-next" onClick={() => navigate(`/dispatch/job/${nextShoot.id}`)}>Open next shoot</DispatchButton>
      : <DispatchButton variant="main" data-testid="schedule-availability" onClick={() => navigate('/dispatch/account')}>Set availability</DispatchButton>;

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100%', color: T.moon, fontFamily: F.heading }}>
      {/* Sticky week header */}
      <div style={{ backgroundColor: T.night, paddingLeft: px, paddingRight: px, paddingTop: 16, paddingBottom: 0, position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <p style={{ fontSize: 22, color: T.moon, fontFamily: F.heading, letterSpacing: -0.4, margin: 0 }}>Week {weekNum}</p>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { label: 'Previous week', icon: <ChevronLeft size={14} color={T.moonMute} strokeWidth={1.5} />, fn: () => goWeek(-1) },
              { label: 'Next week', icon: <ChevronRight size={14} color={T.moonMute} strokeWidth={1.5} />, fn: () => goWeek(1) },
            ].map(({ label, icon, fn }, i) => (
              <button key={i} onClick={fn} aria-label={label} className="bx-tap" style={{ width: 44, height: 44, padding: 0, ...bd(T.nightHair), borderRadius: 2, backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
              </button>
            ))}
          </div>
        </div>

        {/* 7-day grid */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
          {weekDays.map((day, i) => {
            const iso = toISO(day);
            const isSelected = iso === selected;
            const isToday = iso === todayISO;
            const count = jobsForDate(iso).length;
            return (
              <button
                key={iso}
                className="bx-tap"
                onClick={() => setSelected(iso)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  paddingTop: 10, paddingBottom: 10, minWidth: 0, minHeight: 44, ...bd(T.nightHair), borderRadius: 2,
                  backgroundColor: isSelected ? T.moon : 'transparent', cursor: 'pointer',
                }}
              >
                <span style={{ fontFamily: F.heading, fontSize: 11, color: isSelected ? T.night : T.moonMute, marginBottom: 4 }}>
                  {DAY_LABELS[i]}
                </span>
                <span style={{ fontFamily: F.mono, fontSize: 14, color: isSelected ? T.night : (isToday ? T.moon : T.moonFaint), textDecoration: isToday && !isSelected ? 'underline' : 'none', textUnderlineOffset: 3 }}>
                  {day.getDate()}
                </span>
                <div style={{ display: 'flex', gap: 2, justifyContent: 'center', marginTop: 5, height: 4 }}>
                  {count > 0 ? Array.from({ length: Math.min(count, 4) }).map((_, j) => (
                    <div key={j} style={{ width: 3, height: 3, borderRadius: 2, backgroundColor: isSelected ? T.night : T.moonMute }} />
                  )) : <div style={{ height: 4 }} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Day label */}
      <div style={{ paddingLeft: px, paddingRight: px, paddingTop: 14, paddingBottom: 14 }}>
        <p style={{ fontFamily: F.heading, fontSize: 13, fontWeight: 500, color: T.moonFaint, letterSpacing: 0, margin: 0 }}>
          {selectedLabel} · {selectedJobs.length} shoot{selectedJobs.length !== 1 ? 's' : ''}
        </p>
        <div style={{ marginTop: 14 }}>{mainAction}</div>
      </div>

      {/* Timeline */}
      {!loading && selectedJobs.length === 0 ? (
        <div style={{ paddingTop: 60, textAlign: 'center' }}>
          <p style={{ color: T.moonFaint, fontSize: 13 }}>No jobs on this day</p>
        </div>
      ) : (
        <div style={{ paddingLeft: px, paddingRight: px, paddingBottom: 32 }}>
          {selectedJobs.map((job, i) => {
            const pill = pillForStatus(job.status);
            return (
              <div
                key={job.id}
                className="bx-tap"
                onClick={() => navigate(`/dispatch/job/${job.id}`)}
                style={{ display: 'flex', gap: 14, paddingTop: 16, paddingBottom: 16, cursor: 'pointer', ...(i < selectedJobs.length - 1 ? { borderBottom: `1px solid ${T.nightHair}` } : {}) }}
              >
                <span style={{ fontFamily: F.mono, fontSize: 12, color: T.moonFaint, width: 44, paddingTop: 2, flexShrink: 0 }}>
                  {fmtTime(job.time_window_start)}
                </span>
                <div style={{ width: 1, backgroundColor: T.nightHair, position: 'relative', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 6, left: -3, width: 7, height: 7, borderRadius: 4, backgroundColor: pill.dot }} />
                </div>
                <div style={{ flex: 1, paddingLeft: 8 }}>
                  <p style={{ fontSize: 14, color: T.moon, fontFamily: F.heading, marginBottom: 3 }}>{job.address}</p>
                  <p style={{ fontSize: 11, color: T.moonFaint, margin: '0 0 8px 0' }}>{job.job_type?.replace(/_/g, ' ')}</p>
                  <span style={{ fontSize: 12, color: pill.fg }}>{pill.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
